import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    parseLegacyPhoneNumber,
    formatPhoneAsYouType,
    formatNationalPhoneNumber,
    isValidPhoneNumber,
    getMaxNationalPhoneDigits,
    DEFAULT_COUNTRY
} from '@honeybook/i18n';
import HBInput from '../HBInput/HBInput';
import HBCombobox from '../HBCombobox/HBCombobox';
import HBCountryFlag from '../HBCountryFlag/HBCountryFlag';
import { ComboboxItem, ComboboxSection } from '../HBCombobox/HBCombobox.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { useHBUI } from '../HBUIProvider/HBUIProvider';
import { TControlSize } from '../../tokens/types';
import { ICountry, HBPhoneInputProps, CountryCode } from './HBPhoneInput.types';
import {
    StyledPhoneInputWrapper,
    StyledCountrySelector,
    StyledNumberInputWrapper
} from './HBPhoneInput.styles';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';

const COUNTRY_TRIGGER_WIDTH: Record<TControlSize, number> = {
    sm: 96,
    md: 105,
    lg: 110
};

function countryToComboboxItem(country: ICountry): ComboboxItem<ICountry> {
    return {
        value: country,
        label: country.name,
        description: country.dialCode,
        prefix: <HBCountryFlag country={country.code} aria-hidden />,
        searchTerms: [
            ...(country.searchTerms ?? []),
            country.code,
            country.dialCode
        ],
        testIdName: country.code
    };
}

const HBPhoneInput = ({
    value,
    onChange,
    onValidationChange,
    countries,
    suggestedCountryCodes,
    suggestedSectionLabel = 'Suggested',
    allCountriesSectionLabel = 'All countries',
    countrySearchPlaceholder = 'Search',
    placeholder,
    numberInputLabel = 'Phone number',
    size = 'sm',
    disabled = false,
    width,
    name = 'national-phone-number',
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    error = false,
    testId,
    testIdName
}: HBPhoneInputProps) => {
    const isInvalid = isAriaInvalid(error, ariaInvalid);

    const effectiveDefaultCountry: CountryCode =
        (suggestedCountryCodes?.[0] as CountryCode | undefined) ??
        DEFAULT_COUNTRY;

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'phone-input',
        componentName: 'HBPhoneInput'
    });

    const { isHintedEllipsis } = useHBUI();

    // Parse the incoming value (international or legacy format) into structured components.
    const parsedValue = useMemo(
        () =>
            value
                ? parseLegacyPhoneNumber(value, effectiveDefaultCountry)
                : null,
        [value, effectiveDefaultCountry]
    );

    // selectedCountry is internal state so the country selector retains its value
    // even while the number field is empty (value = '').
    const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(
        () => {
            const code = parsedValue?.countryCode ?? effectiveDefaultCountry;
            return countries.find(c => c.code === code) ?? null;
        }
    );

    // Sync selectedCountry when the external value changes to a different country.
    // Functional updater avoids a re-render when the country code hasn't changed.
    const parsedCountryCode = parsedValue?.countryCode;
    useEffect(() => {
        if (!parsedCountryCode) {
            return;
        }
        setSelectedCountry(prev => {
            if (prev?.code === parsedCountryCode) {
                return prev;
            }
            return countries.find(c => c.code === parsedCountryCode) ?? prev;
        });
    }, [parsedCountryCode, countries]);

    // Sync selectedCountry when effectiveDefaultCountry changes (e.g. async geo detection)
    // and there is no value yet — avoids overriding a country the user already chose.
    useEffect(() => {
        if (!value) {
            setSelectedCountry(
                countries.find(
                    country => country.code === effectiveDefaultCountry
                ) ?? null
            );
        }
    }, [effectiveDefaultCountry, countries, value]);

    const sections: ComboboxSection<ICountry>[] = useMemo(() => {
        const allAlphabetized = [...countries].sort((a, b) =>
            a.name.localeCompare(b.name)
        );
        const result: ComboboxSection<ICountry>[] = [];

        if (suggestedCountryCodes?.length) {
            const suggested = suggestedCountryCodes
                .map(code => countries.find(c => c.code === code))
                .filter((c): c is ICountry => !!c);
            if (suggested.length) {
                result.push({
                    id: 'suggested',
                    title: suggestedSectionLabel,
                    items: suggested.map(countryToComboboxItem)
                });
            }
        }

        result.push({
            id: 'all',
            title: allCountriesSectionLabel,
            items: allAlphabetized.map(countryToComboboxItem)
        });

        return result;
    }, [
        countries,
        suggestedCountryCodes,
        suggestedSectionLabel,
        allCountriesSectionLabel
    ]);

    // parsedValue?.nationalNumber covers complete/valid numbers.
    // During typing, incomplete values don't parse — fall back to stripping the
    // selected country's dial code, which handleNumberChange always prepends.
    // When the value can't be resolved at all (e.g. domestic format without a matching
    // country hint), show the raw digits so the user can see what failed to parse.
    const dialCodeStripped =
        selectedCountry && value.startsWith(selectedCountry.dialCode)
            ? value.slice(selectedCountry.dialCode.length)
            : null;
    const nationalNumber =
        parsedValue?.nationalNumber ??
        dialCodeStripped ??
        value.replace(/\D/g, '');

    // Formatted display value — visual only, never stored or emitted.
    const displayValue = useMemo(() => {
        if (!nationalNumber) {
            return '';
        }
        if (!selectedCountry) {
            return nationalNumber;
        }
        // For complete/parseable numbers use formatNational() so the trunk prefix is
        // included in the display (e.g. '054-921-3923' for IL, '020 7123 4567' for GB).
        // AsYouType alone can't recover the trunk prefix from nationalNumber (which has
        // it stripped), so it only formats correctly for NANP (+1) where there's no trunk.
        if (parsedValue) {
            const national = formatNationalPhoneNumber(
                value,
                selectedCountry.code
            );
            if (national !== null) {
                return national;
            }
            // Fall through to AsYouType for values that don't parse cleanly.
        }
        // For partial/in-progress values, and for domestic-format values (e.g. '07700900123'
        // loaded directly without a +44 prefix), AsYouType with the selected country formats
        // correctly — it understands the national trunk prefix for every country.
        return formatPhoneAsYouType(nationalNumber, selectedCountry.code);
    }, [nationalNumber, parsedValue, selectedCountry, value]);

    const handleCountryChange = useCallback(
        (country: ICountry) => {
            setSelectedCountry(country);
            // Re-emit with the new country's dial code, keeping the national number unchanged.
            if (nationalNumber) {
                onChange(`${country.dialCode}${nationalNumber}`);
            }
        },
        [onChange, nationalNumber]
    );

    const handleNumberChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (disabled || !selectedCountry) {
                return;
            }

            const rawInput = e.target.value;

            // Paste or autocomplete supplied an international number — parse it and
            // split across the two fields rather than treating the dial code as digits.
            if (rawInput.startsWith('+')) {
                const parsed = parseLegacyPhoneNumber(
                    rawInput,
                    effectiveDefaultCountry
                );
                if (parsed?.nationalNumber) {
                    const matchedCountry = countries.find(
                        c => c.code === parsed.countryCode
                    );
                    if (
                        matchedCountry &&
                        matchedCountry.code !== selectedCountry.code
                    ) {
                        setSelectedCountry(matchedCountry);
                    }
                    const targetCountry = matchedCountry ?? selectedCountry;
                    onChange(
                        `${targetCountry.dialCode}${parsed.nationalNumber}`
                    );
                    return;
                }
                // parsed is null when the pasted number has an unrecognized calling code.
                // Fall through: strip all non-digits and treat them as national digits for
                // the currently selected country, same as any other input.
            }

            const maxDigits = getMaxNationalPhoneDigits(selectedCountry.code);
            let raw = rawInput.replace(/\D/g, '').slice(0, maxDigits);

            // A backspace that removed only a formatting char (e.g. the ')' in '(415)')
            // leaves the digit string unchanged. Without this guard, reformatting
            // regenerates the same separator and the deletion is a no-op — backspace
            // appears blocked at the area-code boundary. Detect a deletion that didn't
            // change the digits and drop the last one so each backspace makes progress.
            if (
                raw === nationalNumber &&
                rawInput.length < displayValue.length
            ) {
                raw = raw.slice(0, -1);
            }

            onChange(raw ? `${selectedCountry.dialCode}${raw}` : '');
        },
        [
            onChange,
            selectedCountry,
            countries,
            effectiveDefaultCountry,
            disabled,
            nationalNumber,
            displayValue
        ]
    );

    const handleNumberBlur = useCallback(() => {
        if (!onValidationChange) {
            return;
        }
        if (!value) {
            onValidationChange(false);
            return;
        }
        onValidationChange(isValidPhoneNumber(value));
    }, [onValidationChange, value]);

    return (
        <StyledPhoneInputWrapper
            $width={width as string}
            $disabled={disabled}
            data-testid={finalTestId}
        >
            <StyledCountrySelector>
                <HBCombobox<ICountry>
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    sections={sections}
                    aria-label="Country"
                    aria-describedby={ariaDescribedBy}
                    aria-invalid={isInvalid || undefined}
                    size={size}
                    disabled={disabled}
                    width={COUNTRY_TRIGGER_WIDTH[size]}
                    showOverflowTooltip={isHintedEllipsis}
                    renderTriggerContent={selected =>
                        selected ? (
                            <>
                                {/* Not decorative: the trigger shows only the dial
                                    code, so the flag carries the country name for
                                    screen readers and the hover tooltip. */}
                                <HBCountryFlag
                                    country={selected.value.code}
                                    title={selected.value.name}
                                />{' '}
                                {selected.value.dialCode}
                            </>
                        ) : (
                            ''
                        )
                    }
                    searchPlaceholder={countrySearchPlaceholder}
                    menuMinWidth="260px"
                    menuMaxWidth="260px"
                    testIdName={
                        testIdName
                            ? `${testIdName}-country`
                            : 'phone-input-country'
                    }
                />
            </StyledCountrySelector>

            <StyledNumberInputWrapper>
                <HBInput
                    type="tel"
                    id={id}
                    aria-label={
                        ariaLabelledBy
                            ? undefined
                            : ariaLabel ?? numberInputLabel
                    }
                    aria-labelledby={ariaLabelledBy}
                    aria-describedby={ariaDescribedBy}
                    aria-invalid={isInvalid || undefined}
                    name={name}
                    value={displayValue}
                    onChange={handleNumberChange}
                    onBlur={handleNumberBlur}
                    placeholder={placeholder}
                    size={size}
                    disabled={disabled}
                    testIdName={
                        testIdName
                            ? `${testIdName}-number`
                            : 'phone-input-number'
                    }
                    width="100%"
                />
            </StyledNumberInputWrapper>
        </StyledPhoneInputWrapper>
    );
};

export default HBPhoneInput;
